import { useMemo, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Editor } from '@tiptap/react';
import { CommentsProvider } from '../lib/provider';
import { ThreadWithComments } from '@distribute/shared/types';
import { commentsModel } from '../model';

type CommentsProviderProps = {
  threads: ThreadWithComments[];
  updateCommentThread: (id: string, data: Partial<ThreadWithComments>) => void;
};

export const useCommentsProvider = ({
  threads,
  updateCommentThread,
}: CommentsProviderProps) => {
  const dispatch = useDispatch();
  const provider = useMemo(
    () => new CommentsProvider({ dispatch, updateCommentThread }),
    [dispatch, updateCommentThread]
  );
  const editorRef = useRef<Editor>();
  const editor = editorRef.current;
  const isEditorReady = editor && !editor.isDestroyed;
  const resolvedThreadIds = useSelector(
    commentsModel.selectors.selectResolvedThreadIds
  );

  const setEditor = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  useEffect(() => {
    provider.setThreads(threads);
  }, [provider, threads]);

  useEffect(() => {
    const canResolve = isEditorReady && resolvedThreadIds.length > 0;

    if (canResolve) {
      resolvedThreadIds.forEach((id) => {
        editor.commands.resolveThread(id);
      });
      dispatch(commentsModel.actions.setResolvedThreadId([]));
    }
  }, [dispatch, isEditorReady, editor, resolvedThreadIds]);

  return {
    provider,
    setEditor,
  };
};
